import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Field, reduxForm } from 'redux-form';
import { getPasswordValidationDetails } from '@htmniseko/htm-validators/utils/password';
import { Form, Icon } from 'components/HeroSemantic';
import { PasswordValidationHelper } from 'components';
import renderValidationError from 'utils/renderValidationError';
import passwordValidation from './passwordValidation';

const labelClass = [
  'text-[11px] font-semibold leading-4 tracking-[0.04em]',
  'text-[var(--owner-text-muted)] uppercase',
].join(' ');

const inputBaseClass = [
  'min-h-10 w-full rounded-lg border bg-[var(--owner-surface)] pl-3 pr-10 text-sm leading-5',
  'text-[var(--owner-text)] shadow-sm outline-none transition-colors',
  'focus:border-[var(--tenant-primary)] focus:ring-2 focus:ring-[var(--owner-focus-ring)]',
].join(' ');

const errorTextClass = 'text-[12px] leading-4 text-[var(--owner-danger)]';

const submitButtonStyle = disabled => ({
  background: 'color-mix(in srgb, var(--tenant-primary) 78%, #000 22%)',
  border: 'none',
  borderRadius: '8px',
  color: '#FFFFFF',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px',
  minWidth: '140px',
  opacity: disabled ? 0.4 : 1,
  padding: '10px 20px',
  transition: 'opacity 160ms ease, filter 160ms ease',
});

const PasswordField = ({ input, meta, label, autoComplete, onValueChange, helpInline, onFocus }) => {
  const [show, setShow] = useState(false);
  const hasError = (meta.touched || meta.submitFailed) && meta.error;

  const handleChange = event => {
    input.onChange(event);
    if (typeof onValueChange === 'function') {
      onValueChange(event.target.value);
    }
  };

  const handleFocus = event => {
    input.onFocus(event);
    if (typeof onFocus === 'function') {onFocus(event);}
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label className={labelClass} htmlFor={input.name}>
        {label}
        <span style={{ color: 'var(--owner-danger)', marginLeft: '2px' }}>*</span>
      </label>
      <div className="relative">
        <input
          id={input.name}
          name={input.name}
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          className={`${inputBaseClass} ${hasError ? 'border-[var(--owner-danger)]' : 'border-[var(--owner-border-strong)]'}`}
          value={input.value}
          onChange={handleChange}
          onBlur={input.onBlur}
          onFocus={handleFocus}
        />
        <button
          type="button"
          aria-label={show ? 'Hide password' : 'Show password'}
          onClick={() => setShow(prev => !prev)}
          className={[
            'absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-7 w-7',
            'items-center justify-center rounded-md text-[var(--owner-text-muted)]',
            'hover:bg-[var(--owner-surface-muted)] hover:text-[var(--owner-text)]',
          ].join(' ')}
        >
          <Icon name={show ? 'eye-off' : 'eye'} style={{ width: '16px', height: '16px' }} />
        </button>
      </div>
      {hasError && <span className={errorTextClass}>{renderValidationError(meta.error)}</span>}
      {!hasError && helpInline}
    </div>
  );
};

PasswordField.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  autoComplete: PropTypes.string,
  onValueChange: PropTypes.func,
  helpInline: PropTypes.node,
  onFocus: PropTypes.func,
};

const ChangePasswordForm = ({ handleSubmit, onSubmit }) => {
  const { formatMessage } = useIntl();
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [newPasswordFocused, setNewPasswordFocused] = useState(false);

  const passwordDetails = getPasswordValidationDetails(password);
  const passwordIsValid = passwordDetails.hasMinLength && passwordDetails.hasMinCombination;
  const passwordsMatch = repeatPassword.length > 0 && password === repeatPassword;
  const passwordsDoNotMatch = repeatPassword.length > 0 && password !== repeatPassword;

  const showRequirementsPanel = newPasswordFocused || password.length > 0;

  const canSubmit = currentPassword.length > 0 &&
    password.length > 0 &&
    passwordIsValid &&
    passwordsMatch &&
    currentPassword !== password;

  const matchHint = repeatPassword.length > 0 ? (
    <span
      className="inline-flex items-center gap-1.5 text-[12px] leading-4"
      style={{ color: passwordsMatch ? 'var(--owner-success)' : 'var(--owner-danger)' }}
    >
      <Icon name={passwordsMatch ? 'check' : 'warning circle'} style={{ width: '14px', height: '14px' }} />
      {passwordsMatch ?
        formatMessage({ id: 'page.user.changePassword.passwordsMatch', defaultMessage: 'Passwords match' })
        : formatMessage({ id: 'page.user.changePassword.passwordsDoNotMatch', defaultMessage: "Passwords don't match" })}
    </span>
  ) : null;

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className="mx-auto" style={{ maxWidth: '880px' }}>
      <div className="grid grid-cols-1 gap-x-8 gap-y-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex flex-col gap-5">
          <Field
            name="currentPassword"
            component={PasswordField}
            label={formatMessage({ id: 'page.user.changePassword.form.label.currentPassword' })}
            autoComplete="current-password"
            onValueChange={setCurrentPassword}
          />
          <Field
            name="password"
            component={PasswordField}
            label={formatMessage({ id: 'page.user.changePassword.form.label.newPassword' })}
            autoComplete="new-password"
            onValueChange={setPassword}
            onFocus={() => setNewPasswordFocused(true)}
          />
          <Field
            name="repeatPassword"
            component={PasswordField}
            label={formatMessage({ id: 'page.user.changePassword.form.label.repeatPassword' })}
            autoComplete="new-password"
            onValueChange={setRepeatPassword}
            helpInline={matchHint}
          />
        </div>
        {showRequirementsPanel && (
          <div className="lg:row-start-1 lg:col-start-2">
            <PasswordValidationHelper password={password} />
          </div>
        )}
      </div>
      <div className="mt-8 flex items-center gap-3">
        <button
          type="submit"
          disabled={!canSubmit}
          style={submitButtonStyle(!canSubmit)}
        >
          <FormattedMessage id="general.submit" />
        </button>
        {!canSubmit && password.length > 0 && passwordsDoNotMatch && (
          <span className={errorTextClass}>
            <FormattedMessage
              id="page.user.changePassword.passwordsDoNotMatch"
              defaultMessage="Passwords don't match"
            />
          </span>
        )}
      </div>
    </Form>
  );
};

ChangePasswordForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default reduxForm({ form: 'changePassword', validate: passwordValidation })(ChangePasswordForm);
